/**
 * Одно замечание контроля качества: кто, когда, оценка, текст и голос с расшифровкой, к чему
 * привязано, и весь путь до базы знаний — статус, решение модели и почему, итог «внесено в карточку
 * N, vM» со ссылкой на основания. Своё до разбора — изменить или удалить.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronDown, ChevronRight, MessageSquare, Pencil, Phone, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { ACTION_LABEL, audioUrl, deleteRemark, editRemark, ITEM_STATUS_LABEL, markLabel, MARKS, needsAttention, type QcMark, type QcRemark } from "@/lib/qc";

const MARK_COLOR = { good: "#2e9e4f", bad: "#e53935", note: "var(--tg-primary)" } as const;

export const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

/** Переход к объекту в открытой ленте: прокрутить и подсветить; нет в ленте — сказать. */
export function jumpToRef(kind: "message" | "call", id: number): boolean {
  const el = document.getElementById(kind === "message" ? `msg-${id}` : `qc-call-${id}`);
  if (!el) return false;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.animate([{ backgroundColor: "rgba(51,144,236,.28)" }, { backgroundColor: "rgba(51,144,236,0)" }], { duration: 1600, easing: "ease-out" });
  return true;
}

/** Подпись источника: «10:11 · Мы: «ищем мастера…»» / «Звонок 12:34 · 4:18». */
function refLabel(r: QcRemark, ref: { kind: "message" | "call"; id: number }): string {
  const x = r.context?.items.find((i) => i.kind === ref.kind && i.id === ref.id);
  if (!x) return ref.kind === "call" ? "звонок" : "сообщение";
  const t = x.date ? x.date.slice(11, 16) : "";
  if (ref.kind === "call") {
    const d = x.duration ?? 0;
    return `Звонок ${t} · ${Math.floor(d / 60)}:${String(d % 60).padStart(2, "0")}`;
  }
  const кто = x.out ? "Мы" : "Клиент";
  const начало = (x.text || "").replace(/\s+/g, " ").slice(0, 28);
  return `${t} · ${кто}: «${начало}${(x.text || "").length > 28 ? "…" : ""}»`;
}

export function RemarkCard({
  r,
  focused,
  inChat,
  editor,
  invalidate,
  onJump,
}: {
  r: QcRemark;
  focused?: boolean;
  /** Карточка в панели чата: ссылки на объекты прокручивают ленту; иначе — открывают чат. */
  inChat?: boolean;
  /** Редактор базы знаний: ссылка на пункт ченджлога. */
  editor?: boolean;
  invalidate: unknown[];
  /** Перед переходом к объекту в ленте (панель на телефоне закрывается). */
  onJump?: () => void;
}) {
  const client = useQueryClient();
  const [ctx, setCtx] = useState(false);
  const [long, setLong] = useState(false);
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(r.text);
  const [transcript, setTranscript] = useState(r.transcript);
  const [mark, setMark] = useState<QcMark>(r.mark);
  const [miss, setMiss] = useState("");
  const done = () => void client.invalidateQueries({ queryKey: invalidate });
  const save = useMutation({ mutationFn: () => editRemark(r.id, { text, mark, transcript }), onSuccess: () => (setEdit(false), done()) });
  const drop = useMutation({ mutationFn: () => deleteRemark(r.id), onSuccess: done });
  const attention = needsAttention(r);
  return (
    <article
      className={`rounded-[12px] border px-3 py-2.5 text-[13.5px] ${focused ? "border-[var(--tg-primary)] ring-2 ring-[var(--tg-primary)]/20" : "border-border"} ${attention ? "bg-[#e53935]/5" : ""}`}
      data-annot="замечание контроля качества"
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-fg-muted">
        <span className="flex items-center gap-1 font-medium text-fg">
          <span className="size-2 rounded-full" style={{ background: MARK_COLOR[r.mark] }} />
          {markLabel(r.mark)}
        </span>
        <span>№{r.id}</span>
        <span>{r.author}</span>
        <span>{when(r.date)}</span>
        {r.editable && !edit && (
          <span className="ml-auto flex gap-1">
            <button
              type="button"
              onClick={() => {
                // Поля — из свежих данных: расшифровка могла прийти после первого показа.
                setText(r.text);
                setTranscript(r.transcript);
                setMark(r.mark);
                setEdit(true);
              }}
              title="Изменить — пока не взято в разбор"
              className="grid size-8 place-items-center rounded hover:bg-bg-hover"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => window.confirm("Удалить замечание?") && drop.mutate()}
              title="Удалить — пока не взято в разбор"
              className="grid size-8 place-items-center rounded hover:bg-bg-hover"
            >
              <Trash2 size={14} />
            </button>
          </span>
        )}
      </header>
      {edit ? (
        <div className="mt-2 flex flex-col gap-1.5">
          <div className="flex gap-1">
            {MARKS.map((m) => (
              <button key={m.id} type="button" onClick={() => setMark(m.id)} className={`rounded-full px-2.5 py-0.5 text-[12px] ${mark === m.id ? "bg-accent text-white" : "bg-bg-subtle"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="rounded border border-border bg-transparent px-2 py-1" placeholder="Текст замечания" />
          {r.audio && r.status !== "waiting_voice" && (
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={3}
              className="rounded border border-border bg-transparent px-2 py-1"
              placeholder="Расшифровка голоса — исправьте ошибки распознавания"
            />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className="rounded bg-accent px-3 py-1 text-white disabled:opacity-50">
              Сохранить
            </button>
            <button type="button" onClick={() => setEdit(false)} className="rounded px-3 py-1 hover:bg-bg-hover">
              Отмена
            </button>
          </div>
          {save.isError && <p className="text-[12px] text-err">{save.error.message}</p>}
        </div>
      ) : (
        <>
          {r.text && <p className="mt-1.5 whitespace-pre-wrap break-words">{r.text}</p>}
          {r.audio && (
            <div className="mt-1.5">
              <audio src={audioUrl("remark", r.id)} controls preload="none" className="h-8 w-full max-w-[320px]" />
              {r.transcript ? (
                <p className="mt-1 whitespace-pre-wrap break-words text-fg-muted">«{r.transcript}»</p>
              ) : (
                <p className="mt-1 text-[12px] text-fg-muted">{r.status === "waiting_voice" ? "Расшифровывается…" : r.error}</p>
              )}
            </div>
          )}
        </>
      )}
      {r.closed && <p className="mt-1.5 text-[12px] text-fg-muted">Источник — в закрытой для вас группе: переписка скрыта.</p>}
      <div className="mt-2 flex flex-wrap gap-1">
        {r.refs.length === 0 && (
          <button
            type="button"
            onClick={() => !inChat && window.open(`/chats/${r.chat_id}`, "_blank")}
            disabled={inChat}
            className="flex items-center gap-1 rounded-full bg-bg-subtle px-2 py-0.5 text-[12px] enabled:hover:bg-bg-hover"
            title={inChat ? "Сообщения не отмечали — замечание ко всему чату на момент записи" : "Открыть чат"}
          >
            <MessageSquare size={11} /> Ко всему чату
          </button>
        )}
        {r.refs.map((ref) => (
          <button
            key={`${ref.kind}-${ref.id}`}
            type="button"
            onClick={() => {
              if (inChat) {
                onJump?.();
                window.setTimeout(
                  () => setMiss(jumpToRef(ref.kind, ref.id) ? "" : "Этого объекта нет в загруженной ленте — он старше; откройте «Что отмечено»"),
                  50,
                );
              }
              else window.open(`/chats/${ref.chat_id}${ref.kind === "message" ? `?msg=${ref.id}` : ""}`, "_blank");
            }}
            className="flex items-center gap-1 rounded-full bg-bg-subtle px-2 py-0.5 text-[12px] hover:bg-bg-hover"
            title={inChat ? "Показать в ленте" : "Открыть чат"}
          >
            {ref.kind === "call" ? <Phone size={11} /> : <MessageSquare size={11} />}
            {refLabel(r, ref)}
          </button>
        ))}
        {r.context && (
          <button type="button" onClick={() => setCtx((v) => !v)} className="flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[12px] text-fg-muted hover:bg-bg-hover">
            {ctx ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {r.context.whole_chat ? "Последние реплики" : "Что отмечено"}
          </button>
        )}
      </div>
      {miss && <p className="mt-1 text-[12px] text-fg-muted">{miss}</p>}
      {drop.isError && <p className="mt-1 text-[12px] text-err">{drop.error.message}</p>}
      {ctx && r.context && (
        <div className="mt-1.5 flex flex-col gap-1 rounded-[8px] bg-bg-subtle px-2 py-1.5 text-[12.5px]">
          {r.context.before.map((x) => (
            <div key={`b-${x.kind}-${x.id}`} className="text-fg-muted">
              <b>{x.out ? "Мы" : "Клиент"}:</b> {x.text.slice(0, 300)}
            </div>
          ))}
          {r.context.items.map((x) => (
            <div key={`i-${x.kind}-${x.id}`} className="border-l-2 border-accent pl-1.5">
              <b>{x.kind === "call" ? `Звонок ${x.out ? "исходящий" : "входящий"}${x.author ? `, ${x.author}` : ""}` : x.out ? x.author || "Мы" : "Клиент"}:</b>{" "}
              {x.text ? (long ? x.text : x.text.slice(0, 1200)) : <i>расшифровки ещё нет</i>}
              {x.text.length > 1200 && (
                <button type="button" onClick={() => setLong((v) => !v)} className="ml-1 text-accent hover:underline">
                  {long ? "свернуть" : "показать полностью"}
                </button>
              )}
              {x.kind === "call" && (
                <audio src={`/api/calls/${x.id}/record`} controls preload="none" className="mt-1 h-7 w-full max-w-[300px]" />
              )}
            </div>
          ))}
        </div>
      )}
      <footer className="mt-2 border-t border-border pt-1.5 text-[12.5px]">
        <div className={`font-medium ${attention ? "text-err" : r.status === "applied" ? "text-ok" : ""}`}>{r.status_text}</div>
        {r.item && (
          <div className="mt-0.5 text-fg-muted">
            Решение модели: {ACTION_LABEL[r.item.action]}
            {r.item.title ? ` «${r.item.title}»` : ""} · {ITEM_STATUS_LABEL[r.item.status]}
            {r.item.revision > 1 ? ` · доработка ${r.item.revision - 1}` : ""}
            {r.item.why && <div className="mt-0.5 italic">«{r.item.why}»</div>}
            {editor && (
              <Link to={`/qc?changelog=${r.item.changelog_id}&item=${r.item.id}`} className="text-accent hover:underline">
                Открыть пункт ченджлога
              </Link>
            )}
          </div>
        )}
        {r.card && (r.status === "applied" || r.status === "no_action") && (
          <Link to={`/qc?card=${r.card.id}`} className="mt-1 flex flex-wrap items-center gap-1 text-accent hover:underline" title="Основания карточки: какие замечания и версии">
            <BookOpen size={13} />
            {r.status === "no_action" ? "Правило уже есть" : "Внесено"}: «{r.card.title}»{r.card.version ? `, v${r.card.version}` : ""}
            {r.card.version && r.card.current !== r.card.version ? ` · сейчас v${r.card.current}` : ""}
            {r.status === "applied" && <span className="text-fg-muted"> · поисковая копия {r.card.search}</span>}
          </Link>
        )}
        {r.status === "in_changelog" && r.item?.card_title && (
          <div className="mt-0.5 text-fg-muted">Цель решения: «{r.item.card_title}» — ещё не внесено</div>
        )}
        {r.status === "in_changelog" && !r.item && <div className="mt-0.5 text-fg-muted">Модель разбирает…</div>}
      </footer>
    </article>
  );
}
